import { useMemo } from 'react';

import sumBy from 'lodash/sumBy';

import { MOST_EXPENSIVE_ITEM_COLOR_MAP } from '@cast/constants';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { usePaginatedWorkloadCostReportQuery } from '@cast/split-me/hooks';

import type { TopMostExpensiveGroup } from '@cast/split-me/types';

import { useComputeAdjustedDateRange } from './useComputeAdjustedDateRange';
import { mapTopMostExpensiveWorkloadDatapoints } from '../_utils/mapTopMostExpensiveWorkloadDatapoints';

const PAGE_LIMIT = 5;

export const useInventoryComputeCostReport = () => {
  const { cluster } = useCluster();
  const { adjustedDateRange, unit, stepSeconds, timeZone } =
    useComputeAdjustedDateRange();

  const [from, to] = adjustedDateRange;

  const {
    workloads,
    count,
    noDataReason,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = usePaginatedWorkloadCostReportQuery({
    clusterId: cluster.id,
    startTime: from.toISOString(),
    endTime: to.toISOString(),
    useListingPrices: false,
    includeIdleCosts: false,
    pageLimit: PAGE_LIMIT,
    stepSeconds,
  });

  const datapoints = useMemo(
    () =>
      mapTopMostExpensiveWorkloadDatapoints({
        range: adjustedDateRange,
        workloads,
        timezone: timeZone,
        unit,
      }),
    [adjustedDateRange, workloads, timeZone, unit]
  );

  const topItems = useMemo(
    () =>
      workloads.map(({ workloadName, workloadType, namespace }, index) => ({
        id: `${workloadName}-${workloadType}-${namespace}`,
        name: workloadName!,
        color: MOST_EXPENSIVE_ITEM_COLOR_MAP.colorByUsageRank[index],
        cost: sumBy(datapoints, ({ isForecast, values }) =>
          !isForecast && values && workloadName ? values[workloadName] ?? 0 : 0
        ),
      })) as TopMostExpensiveGroup[],
    [datapoints, workloads]
  );

  return {
    datapoints,
    topItems,
    workloads,
    count,
    noDataReason,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
  };
};
